/**
 * Comments System - Type Definitions
 * Polymorphic comments that can be attached to any entity type
 */

// Supported entity types from s4me_entity_registry
export type EntityType = 
  | 'patient' 
  | 'provider' 
  | 'session' 
  | 'clinic' 
  | 'task' 
  | 'encounter' 
  | 'invoice' 
  | 'claim' 
  | 'billing_encounter'
  | 'document' 
  | 'comment' 
  | 'incident_report'
  | 'insurance_payment' 
  | 'patient_payment';

// Reaction types available for comments
export type ReactionType = 
  | 'like' 
  | 'helpful' 
  | 'resolved' 
  | 'important' 
  | 'question';

/**
 * User information from /auth/userinfo endpoint
 */
export interface UserInfo {
  id: number;
  first_name: string;
  last_name: string;
  role_id: number;
  role_name: string;
  is_super_admin: boolean;
  profile_pic_url: string;
  userType?: {
    id: number;
    type: string;
  };
  assignedClinics?: any[];
  userClinicDTOS?: any[];
  highestUserType?: string;
}

/**
 * Reaction on a comment
 */
export interface Reaction {
  type: ReactionType;
  user_id: number;
}

/**
 * Comment response from API
 */
export interface Comment {
  id: number;
  entity_type: string;
  entity_id: string;
  comment_text: string;
  parent_comment_id: number | null;
  thread_depth: number;
  is_internal_only: boolean;
  is_pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  created_by: number;
  updated_at: string | null;
  updated_by: number | null;
  deleted_at?: string | null;
  deleted_by?: number | null;
  reactions?: Reaction[];
  mentions?: number[];
}

/**
 * Comment with enriched user information
 */
export interface CommentWithUser extends Comment {
  created_by_user?: UserInfo;
  updated_by_user?: UserInfo;
}

/** Nested comment for threaded UI (built from flat API list). */
export interface CommentThreadNode {
  comment: CommentWithUser;
  replies: CommentThreadNode[];
}

/**
 * Request to create a new comment
 */
export interface CreateCommentRequest {
  entity_type: string;
  entity_id: string;
  comment_text: string;
  parent_comment_id?: number;
  is_internal_only?: boolean;
  is_pinned?: boolean;
  mentioned_user_ids?: number[];
}

/**
 * Request to update an existing comment
 */
export interface UpdateCommentRequest {
  comment_text?: string;
  is_internal_only?: boolean;
  is_pinned?: boolean;
}

/**
 * Request to add a reaction
 */
export interface ReactionRequest {
  reaction_type: ReactionType;
}

/**
 * Configuration for opening comments panel
 */
export interface CommentsConfig {
  entityType: EntityType;
  entityId: string;
}

export interface AttachmentTagContext {
  primaryTags: string[];
  relatedTags: string[];
}

/**
 * API response for comment creation
 */
export interface CreateCommentResponse {
  success: boolean;
  comment_id: number;
  message: string;
}

/**
 * Standard API success response
 */
export interface ApiSuccessResponse {
  success: boolean;
  message: string;
}
